import type { DashboardTheme, ThemeMode } from './types';

export const DEFAULT_DASHBOARD_THEME: DashboardTheme = {
  primaryColor: '#0b57d0',
};

export const THEME_PRESETS: DashboardTheme[] = [
  DEFAULT_DASHBOARD_THEME,
  { primaryColor: '#155eef' },
  { primaryColor: '#0f766e' },
  { primaryColor: '#7c3aed' },
  { primaryColor: '#be123c' },
  { primaryColor: '#334155' },
];

function clamp(value: number, min = 0, max = 255): number {
  return Math.max(min, Math.min(max, value));
}

export function normalizeHexColor(value: string | undefined, fallback: string): string {
  const text = String(value ?? '').trim();
  if (/^#[0-9a-f]{6}$/i.test(text)) return text.toLowerCase();
  if (/^#[0-9a-f]{3}$/i.test(text)) {
    const [, r = '0', g = '0', b = '0'] = text;
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return fallback;
}

export function normalizeDashboardTheme(theme?: Partial<DashboardTheme> | null): DashboardTheme {
  return {
    primaryColor: normalizeHexColor(
      theme?.primaryColor ?? theme?.baseColor ?? undefined,
      DEFAULT_DASHBOARD_THEME.primaryColor,
    ),
  };
}

export function parseHex(value: string): [number, number, number] {
  const normalized = normalizeHexColor(value, '#000000').slice(1);
  return [
    Number.parseInt(normalized.slice(0, 2), 16),
    Number.parseInt(normalized.slice(2, 4), 16),
    Number.parseInt(normalized.slice(4, 6), 16),
  ];
}

export function toHex(rgb: [number, number, number]): string {
  return `#${rgb.map((value) => clamp(Math.round(value)).toString(16).padStart(2, '0')).join('')}`;
}

export function mixColor(a: string, b: string, ratio: number): string {
  const left = parseHex(a);
  const right = parseHex(b);
  const amount = Math.max(0, Math.min(1, ratio));
  return toHex([
    left[0] + (right[0] - left[0]) * amount,
    left[1] + (right[1] - left[1]) * amount,
    left[2] + (right[2] - left[2]) * amount,
  ]);
}

export function relativeLuminance(color: string): number {
  const [r, g, b] = parseHex(color).map((channel) => {
    const value = channel / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * (r ?? 0) + 0.7152 * (g ?? 0) + 0.0722 * (b ?? 0);
}

export function contrastRatio(a: string, b: string): number {
  const left = relativeLuminance(a);
  const right = relativeLuminance(b);
  const lighter = Math.max(left, right);
  const darker = Math.min(left, right);
  return (lighter + 0.05) / (darker + 0.05);
}

export function readableTextColor(background: string): string {
  return contrastRatio(background, '#ffffff') >= contrastRatio(background, '#0f172a')
    ? '#ffffff'
    : '#0f172a';
}

export function resolveThemeMode(mode: ThemeMode, prefersDark: boolean): 'light' | 'dark' {
  return mode === 'system' ? (prefersDark ? 'dark' : 'light') : mode;
}

function contrastAdjustedColor(color: string, background: string, target: '#ffffff' | '#000000', minimum = 4.5): string {
  let adjusted = normalizeHexColor(color, DEFAULT_DASHBOARD_THEME.primaryColor);
  for (let step = 0; step <= 16 && contrastRatio(adjusted, background) < minimum; step += 1) {
    adjusted = mixColor(adjusted, target, 0.12);
  }
  return adjusted;
}

export function adaptedPrimaryColor(theme: DashboardTheme, resolvedMode: 'light' | 'dark'): string {
  const primary = normalizeDashboardTheme(theme).primaryColor;
  return resolvedMode === 'dark'
    ? contrastAdjustedColor(primary, '#181b20', '#ffffff', 6.2)
    : contrastAdjustedColor(primary, '#ffffff', '#000000', 4.5);
}

export function deriveCssTokens(theme: DashboardTheme, resolvedMode: 'light' | 'dark'): Record<string, string> {
  const primary = adaptedPrimaryColor(theme, resolvedMode);
  const primaryText = readableTextColor(primary);
  const dark = resolvedMode === 'dark';
  const surface = dark ? '#181b20' : '#ffffff';
  const surfaceSoft = dark ? '#20242b' : '#f8fafc';
  const appBg = dark ? '#111318' : '#f6f8fb';
  const border = dark ? '#394453' : '#d9e0ea';
  const primarySoft = dark ? mixColor(primary, '#111318', 0.58) : mixColor(primary, '#ffffff', 0.88);
  const primaryMuted = dark ? mixColor(primary, '#ffffff', 0.42) : mixColor(primary, '#0f172a', 0.18);
  return {
    '--dashboard-primary': primary,
    '--dashboard-primary-text': primaryText,
    '--dashboard-base': primary,
    '--dashboard-contrast': primary,
    '--dashboard-base-text': primaryText,
    '--dashboard-contrast-text': primaryText,
    '--primary': primary,
    '--primary-strong': mixColor(primary, '#000000', dark ? 0.06 : 0.18),
    '--primary-soft': primarySoft,
    '--primary-muted': primaryMuted,
    '--accent': primary,
    '--accent-strong': mixColor(primary, '#000000', dark ? 0.04 : 0.16),
    '--accent-soft': primarySoft,
    '--surface': surface,
    '--surface-soft': surfaceSoft,
    '--app-bg': appBg,
    '--text': dark ? '#edf3fb' : '#172033',
    '--text-strong': dark ? '#ffffff' : '#0f172a',
    '--muted': dark ? '#cbd5e1' : '#475569',
    '--border': border,
    '--shadow': dark ? '0 16px 44px rgba(0,0,0,.36)' : '0 10px 28px rgba(15, 23, 42, 0.075)',
    '--topbar-bg': dark ? '#171a1f' : '#ffffff',
    '--chart-text': dark ? '#edf3fb' : '#334155',
    '--chart-muted': dark ? '#cbd5e1' : '#475569',
    '--chart-grid': dark ? '#465365' : '#dbe3ec',
    '--chart-bg': 'transparent',
    '--map-bg': dark ? '#131820' : '#eef4fb',
    '--map-no-data': dark ? '#3a4656' : '#d2deeb',
    '--map-outline': dark ? '#9aa9bc' : '#5f7085',
    '--map-popup-bg': dark ? '#202938' : '#ffffff',
    '--map-popup-text': dark ? '#f6f9fd' : '#172033',
  };
}

export function dashboardPalette(theme?: DashboardTheme, requested = 5, resolvedMode: 'light' | 'dark' = 'light'): string[] {
  const primary = adaptedPrimaryColor(normalizeDashboardTheme(theme), resolvedMode);
  const count = Math.max(2, Math.min(9, requested));
  const anchors = resolvedMode === 'dark'
    ? [
        mixColor(primary, '#111318', 0.42),
        mixColor(primary, '#111318', 0.24),
        mixColor(primary, '#ffffff', 0.08),
        primary,
        mixColor(primary, '#ffffff', 0.24),
        mixColor(primary, '#ffffff', 0.42),
      ]
    : [
        mixColor(primary, '#ffffff', 0.56),
        mixColor(primary, '#ffffff', 0.38),
        mixColor(primary, '#ffffff', 0.20),
        primary,
        mixColor(primary, '#000000', 0.24),
        mixColor(primary, '#000000', 0.42),
      ];
  return Array.from({ length: count }, (_, index) => {
    const ratio = count === 1 ? 0 : index / (count - 1);
    const position = ratio * (anchors.length - 1);
    const left = Math.floor(position);
    const localRatio = position - left;
    return mixColor(anchors[left] ?? primary, anchors[left + 1] ?? anchors[left] ?? primary, localRatio);
  });
}

export function colorsAreTooClose(_theme?: DashboardTheme): boolean {
  return false;
}
